/*
* downscaleImageToFit utility:
* reduces dimensions (from 2000px down) and JPEG quality until the file is under maxFileSizeMb
* */
export const IMAGE_DOWNSCALE_INITIAL_MAX_SIDE = 2000;
export const IMAGE_DOWNSCALE_MIN_SIDE = 320;
export const IMAGE_DOWNSCALE_SIDE_SCALE = 0.75;
export const IMAGE_DOWNSCALE_DEFAULT_QUALITY = 80;
export const IMAGE_DOWNSCALE_MIN_QUALITY = 50;
export const IMAGE_DOWNSCALE_QUALITY_STEP = 5;

export interface DownscaleImageToFitOptions {
  maxBytes: number;
  maxSide?: number;
  minSide?: number;
  qualityPercent?: number;
  minQualityPercent?: number;
  qualityStep?: number;
  /** When true, encode as JPEG for better compression under size limits. */
  forceJpeg?: boolean;
  sourceType?: string;
  fileName?: string;
}

export interface DownscaleImageToFitResult {
  blob: Blob;
  file: File;
  width: number;
  height: number;
  qualityPercent: number;
  maxSide: number;
  contentType: string;
}

export const loadImageFromBlob = (blob: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = error => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    img.src = url;
  });

export const loadImageFromBase64 = (base64: string, contentType = 'image/jpeg'): Promise<HTMLImageElement> => {
  const dataUrl = base64.startsWith('data:') ? base64 : `data:${contentType};base64,${base64}`;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
};

const encodeCanvasToBlob = (
  canvas: HTMLCanvasElement,
  contentType: string,
  quality?: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      value => {
        if (!value) {
          reject(new Error('Failed to encode image'));
          return;
        }
        resolve(value);
      },
      contentType,
      contentType === 'image/jpeg' ? quality : undefined,
    );
  });

const drawScaledImage = (image: HTMLImageElement, maxSide: number): HTMLCanvasElement => {
  const ratio = Math.min(1, maxSide / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * ratio));
  const height = Math.max(1, Math.round(image.height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas not supported');
  }
  ctx.drawImage(image, 0, 0, width, height);
  return canvas;
};

/**
 * Downscales and re-encodes an image until it fits under maxBytes (or min bounds are reached).
 */
export const downscaleImageToFit = async (
  image: HTMLImageElement,
  options: DownscaleImageToFitOptions,
): Promise<DownscaleImageToFitResult> => {
  const maxBytes = options.maxBytes;
  const minSide = options.minSide ?? IMAGE_DOWNSCALE_MIN_SIDE;
  const minQualityPercent = options.minQualityPercent ?? IMAGE_DOWNSCALE_MIN_QUALITY;
  const qualityStep = options.qualityStep ?? IMAGE_DOWNSCALE_QUALITY_STEP;
  const forceJpeg = options.forceJpeg ?? false;
  const initialMaxSide = options.maxSide ?? IMAGE_DOWNSCALE_INITIAL_MAX_SIDE;
  const initialQualityPercent = options.qualityPercent ?? IMAGE_DOWNSCALE_DEFAULT_QUALITY;
  const sourceType = options.sourceType ?? 'image/jpeg';

  let maxSide = initialMaxSide;
  let qualityPercent = initialQualityPercent;
  let contentType = forceJpeg || sourceType !== 'image/png' ? 'image/jpeg' : 'image/png';

  let blob: Blob | null = null;
  let width = image.width;
  let height = image.height;
  let switchedToJpeg = false;

  while (true) {
    const canvas = drawScaledImage(image, maxSide);
    width = canvas.width;
    height = canvas.height;
    blob = await encodeCanvasToBlob(canvas, contentType, qualityPercent / 100);

    if (blob.size <= maxBytes) {
      break;
    }

    if (maxSide > minSide) {
      maxSide = Math.max(minSide, Math.round(maxSide * IMAGE_DOWNSCALE_SIDE_SCALE));
      continue;
    }

    if (contentType === 'image/jpeg' && qualityPercent > minQualityPercent) {
      qualityPercent = Math.max(minQualityPercent, qualityPercent - qualityStep);
      continue;
    }

    if (contentType === 'image/png' && !switchedToJpeg) {
      contentType = 'image/jpeg';
      switchedToJpeg = true;
      maxSide = initialMaxSide;
      qualityPercent = initialQualityPercent;
      continue;
    }

    break;
  }

  if (!blob) {
    throw new Error('Failed to downscale image');
  }

  const baseName = (options.fileName ?? 'image').replace(/\.[^.]+$/, '');
  const extension = contentType === 'image/png' ? 'png' : 'jpg';
  const file = new File([blob], `${baseName}.${extension}`, { type: contentType });

  return {
    blob,
    file,
    width,
    height,
    qualityPercent,
    maxSide,
    contentType,
  };
};

/*
* From File: size is checked after downscale (large originals are no longer rejected before optimization)
* By URL: oversized images are downscaled the same way before uploadBy URL: oversized images are downscaled the same way before upload
* For the html-editor’s small limit (maxFileSizeMb < 1), JPEG is forced so compression stays reliable
* */
